import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { StyledButton1 } from '../../Common/Buttons/StyledButtons';
import Center from '../../Common/Layouts/Center';
import { themeColor } from '../../Theming';
import { HyperisePlaceholderContainer } from '../index';
import images from '../../Theming/images';
import colors from '../../Theming/colors';
import { useQuery } from '../../Common/Hooks';
import { isIOS, osVersion } from 'react-device-detect';
import i18next from 'i18next';
import clsx from 'clsx';

const Container = styled.div`
  width: 100%;
  font-family: 'Fira Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    'Roboto', 'Palanquin', 'Oxygen', 'Ubuntu', 'Cantarell', 'Droid Sans',
    'Helvetica Neue', sans-serif;

  ol {
    padding: 0;
    padding-left: 1rem;
  }
  ol > li {
    padding-bottom: 0.8rem;
    font-size: small;
  }
  img {
    flex: 1;
    object-fit: cover;
    margin-left: 0.2rem;
  }
  h6 {
    margin: 0.5rem 0;
    padding: 0;
  }
  h4 {
    color: ${themeColor('green')};
  }
`;

const StyledImage = styled.img`
  margin-top: 5px;
  flex: none !important;
  width: auto;
  height: 301px;
`;

const IconContentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const IconContentDescription = styled.div`
  color: ${themeColor('black')};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;
  padding-top: 14px;
`;

const WhatYouNeedText = styled.div`
  font-size: 26px;
  font-style: normal;
  font-weight: Bold;
  line-height: 35px;
  margin-bottom: 11px;
  text-align: center;
`;

const ContentItem = styled.div`
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-bottom: 39px;
  width: 250px;
  display: flex;
  flex-direction: row;
`;

const BodyContentContainer = styled.div`
  width: 100%;
  padding-left: 24px;
  padding-right: 24px;
`;

const AllowBothText = styled.div`
  width: 175px;
  padding-left: 4px;
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-20px);
  }
  60% {
    transform: translateY(-10px);
  }
`;

const StyledTapHereImage = styled.img`
  position: fixed;
  bottom: 4px;

  animation: ${bounce} 2s infinite;

  &.left {
    left: 40px;
  }
  &.right {
    right: 40px;
  }
`;

export const FirstA = styled.span`
  font-size: 14px;
  padding-top: 4px;
  padding-left: 3px;
  padding-right: 1px;
`;

export const SecondA = styled.span`
  font-size: 18px;
`;

interface DescriptionProps {
  overrideBackToAppButtonVisibility?: boolean;
}

const DescriptionWithiOS18Support: React.FC<DescriptionProps> = ({
  overrideBackToAppButtonVisibility,
}) => {
  const { t } = useTranslation();
  const query = useQuery();
  const supportsiOS18 = isIOS && parseInt(osVersion) >= 18;
  const renderArabicVersion = i18next.language === 'ar';
  const [showBackToApp, setShowBackToApp] = useState(false);

  const openApp = useCallback(() => {
    const origin = query.get('origin');
    if (origin) window.location.href = `${origin}://`;
  }, [query]);

  const getStepListImage = (renderArabicVersion: boolean) => {
    return renderArabicVersion
      ? images.partnerpluginsdk_safari_settings_steps_ios_aa_ar
      : images.partnerpluginsdk_safari_settings_steps_ios_aa;
  };

  const getTapIconImage = (renderArabicVersion: boolean) => {
    if (renderArabicVersion) {
      return supportsiOS18
        ? images.partnerpluginsdk_tap_here_ios_18_ar
        : images.partnerpluginsdk_tap_aa_here_ar;
    } else {
      return supportsiOS18
        ? images.partnerpluginsdk_tap_here_ios_18
        : images.partnerpluginsdk_tap_aa_here;
    }
  };

  useEffect(() => {
    if (overrideBackToAppButtonVisibility) {
      setShowBackToApp(overrideBackToAppButtonVisibility);
    }
  }, [overrideBackToAppButtonVisibility]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const messageHandler = (message: any) => {
      if (message && message.data === '') {
        window.location.reload();
      } else if (message) {
        switch (message.data?.type) {
          case 'PluginEnabled': {
            setShowBackToApp(true);
            break;
          }
        }
      }
    };

    window.addEventListener('message', messageHandler);

    return () => window.removeEventListener('message', messageHandler);
  }, []);

  return (
    <Container id="descriptionContainer">
      <BodyContentContainer>
        <WhatYouNeedText>
          <Trans
            t={t}
            i18nKey={'PartnerpluginsdkActivate.followTheseEasyStepsToActivate'}
          />
        </WhatYouNeedText>
        <IconContentContainer>
          <IconContentDescription>
            <ContentItem>
              1.{' '}
              {!supportsiOS18 ? (
                <Trans i18nKey="PartnerpluginsdkActivate.multiStep.tapAa" t={t}>
                  <FirstA>A</FirstA>
                  <SecondA>A</SecondA>
                </Trans>
              ) : (
                t('PartnerpluginsdkActivate.tapToolIcon')
              )}
            </ContentItem>
            <ContentItem>
              2. {t('PartnerpluginsdkActivate.tapManageExtension')}
            </ContentItem>
            <ContentItem>
              3. {t('PartnerpluginsdkActivate.toggleOnTheExtension')}
            </ContentItem>
            <ContentItem>
              4. {t('PartnerpluginsdkActivate.clickAlwaysAllow')}
            </ContentItem>
            <ContentItem>
              5.
              <AllowBothText>
                {t('PartnerpluginsdkActivate.clickAlwaysAllowOnEverySite')}
              </AllowBothText>
            </ContentItem>
          </IconContentDescription>
          <StyledImage
            src={
              supportsiOS18
                ? images.partnerpluginsdk_safari_settings_steps_ios18
                : getStepListImage(renderArabicVersion)
            }
            alt="activationSteps"
          />
        </IconContentContainer>
      </BodyContentContainer>
      <Center flexDirection="column" marginTop={-20}>
        <div id="buttonContainer">
          {showBackToApp && (
            <StyledButton1
              onClick={openApp}
              borderRadius="5"
              backgroundColor={colors.semiDarkGrey}
              color={colors.white}
              fontSize={20}
              padding="1rem"
            >
              {t('PartnerpluginsdkActivate.backToYourAppButton')}
            </StyledButton1>
          )}
        </div>
        <HyperisePlaceholderContainer id="afterButton">
          <p></p>
        </HyperisePlaceholderContainer>
      </Center>
      <StyledTapHereImage
        width={136}
        height={45}
        className={clsx({
          left: !renderArabicVersion,
          right: renderArabicVersion,
        })}
        src={getTapIconImage(renderArabicVersion)}
      />
    </Container>
  );
};

export default DescriptionWithiOS18Support;
